import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Button } from "@/components/PageUI";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Megaphone, Mail, MessageSquare } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

export default function Messages() {
  const { user } = useAuth();
  const [channel, setChannel] = useState("team");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const [annForm, setAnnForm] = useState({ title: "", body: "" });
  const [emailParents, setEmailParents] = useState(true);
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [threadMsgs, setThreadMsgs] = useState([]);
  const [reply, setReply] = useState("");
  const [contacts, setContacts] = useState([]);
  const endRef = useRef(null);

  const loadMsgs = (ch) => api.get(`/messages?channel=${ch}`).then((r) => setMessages(r.data));
  useEffect(() => { loadMsgs(channel); }, [channel]);
  useEffect(() => { api.get("/announcements").then((r) => setAnnouncements(r.data)); }, []);
  useEffect(() => { api.get("/messages/parent-threads").then((r) => setThreads(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/contact-messages").then((r) => setContacts(r.data)).catch(() => {}); }, []);
  const markContactRead = async (c) => {
    if (c.read) return;
    await api.put(`/contact-messages/${c.id}/read`);
    setContacts((list) => list.map((x) => (x.id === c.id ? { ...x, read: true } : x)));
  };
  const delContact = async (id) => {
    await api.delete(`/contact-messages/${id}`);
    setContacts((list) => list.filter((x) => x.id !== id));
  };
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, threadMsgs]);

  const openThread = async (t) => {
    setActiveThread(t);
    const { data } = await api.get(`/messages/parent-thread/${t.thread_id}`);
    setThreadMsgs(data);
  };
  const sendReply = async () => {
    if (!reply.trim() || !activeThread) return;
    await api.post("/messages/parent-reply", { thread_id: activeThread.thread_id, text: reply });
    setReply("");
    const { data } = await api.get(`/messages/parent-thread/${activeThread.thread_id}`);
    setThreadMsgs(data);
    api.get("/messages/parent-threads").then((r) => setThreads(r.data));
  };

  const send = async () => {
    if (!text.trim()) return;
    await api.post("/messages", { channel, text });
    setText(""); loadMsgs(channel);
  };

  const postAnn = async () => {
    if (!annForm.title || !annForm.body) return toast.error("Title and body required");
    try {
      const { data } = await api.post("/announcements", { ...annForm, email_parents: emailParents });
      toast.success(emailParents ? `Posted · emailed ${data.emailed || 0} parents` : "Announcement posted");
      setAnnForm({ title: "", body: "" });
      api.get("/announcements").then((r) => setAnnouncements(r.data));
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Messaging" subtitle="Team chat, parent chat and club announcements." testid="messages-header" />
      <Tabs defaultValue="chat">
        <TabsList data-testid="messages-tabs">
          <TabsTrigger value="chat">Chat</TabsTrigger>
          <TabsTrigger value="parents" data-testid="tab-parent-dms">Parent Messages</TabsTrigger>
          <TabsTrigger value="inbox" data-testid="tab-inbox">Inbox</TabsTrigger>
          <TabsTrigger value="announcements">Announcements</TabsTrigger>
        </TabsList>

        <TabsContent value="parents" className="mt-5">
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-border bg-card p-3 space-y-1 lg:col-span-1 max-h-[55vh] overflow-y-auto" data-testid="parent-threads">
              {threads.length === 0 && <p className="text-sm text-muted-foreground p-4 text-center">No parent messages yet.</p>}
              {threads.map((t) => (
                <button key={t.thread_id} onClick={() => openThread(t)} data-testid={`thread-${t.thread_id}`}
                  className={`w-full text-left rounded-xl p-3 transition-colors duration-150 ${activeThread?.thread_id === t.thread_id ? "bg-primary/15" : "hover:bg-accent"}`}>
                  <p className="font-medium text-sm">{t.parent_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{t.last}</p>
                </button>
              ))}
            </div>
            <div className="rounded-2xl border border-border bg-card flex flex-col h-[55vh] lg:col-span-2">
              {activeThread ? (
                <>
                  <div className="px-5 py-3 border-b border-border font-semibold">{activeThread.parent_name}</div>
                  <div className="flex-1 overflow-y-auto p-5 space-y-3">
                    {threadMsgs.map((m) => (
                      <div key={m.id} className={`flex flex-col ${m.from_parent ? "items-start" : "items-end"}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${m.from_parent ? "bg-accent" : "brand-gradient text-white"}`}><p className="text-sm">{m.text}</p></div>
                        {!m.from_parent && m.seen && <span className="text-[10px] text-muted-foreground mt-0.5">✓ Seen</span>}
                      </div>
                    ))}
                    <div ref={endRef} />
                  </div>
                  <div className="p-3 border-t border-border flex gap-2">
                    <Input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} placeholder="Reply to parent..." data-testid="thread-reply-input" />
                    <Button onClick={sendReply} className="brand-gradient text-white border-0" data-testid="thread-reply-btn"><Send size={16} /></Button>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm"><MessageSquare size={18} className="mr-2" /> Select a conversation</div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="inbox" className="mt-5">
          <div className="space-y-3" data-testid="contact-inbox">
            {contacts.length === 0 && <p className="text-sm text-muted-foreground p-4 text-center">No messages from your public club page yet.</p>}
            {contacts.map((c) => (
              <div key={c.id} onClick={() => markContactRead(c)} data-testid={`contact-${c.id}`}
                className={`rounded-2xl border p-5 cursor-pointer transition-colors duration-150 ${c.read ? "border-border bg-card" : "border-primary/40 bg-primary/5"}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {!c.read && <span className="h-2 w-2 rounded-full bg-primary" data-testid={`contact-unread-${c.id}`} />}
                    <p className="font-medium text-sm">{c.name}</p>
                    <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="text-xs text-secondary hover:underline">{c.email}</a>
                  </div>
                  <p className="text-xs text-muted-foreground shrink-0">{c.created_at?.slice(0, 10)}</p>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{c.message}</p>
                  <button onClick={(e) => { e.stopPropagation(); delContact(c.id); }} className="text-xs text-muted-foreground hover:text-destructive shrink-0 mt-2" data-testid={`del-contact-${c.id}`}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="chat" className="mt-5">
          <div className="flex gap-2 mb-4">
            {["team", "parents"].map((c) => (
              <button key={c} onClick={() => setChannel(c)} data-testid={`channel-${c}`}
                className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition-colors duration-150 ${channel === c ? "brand-gradient text-white" : "bg-accent text-muted-foreground hover:text-foreground"}`}>
                {c} chat
              </button>
            ))}
          </div>
          <div className="rounded-2xl border border-border bg-card flex flex-col h-[55vh]">
            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="chat-messages">
              {messages.length === 0 && <p className="text-center text-sm text-muted-foreground py-10">No messages yet. Say hello 👋</p>}
              {messages.map((m) => {
                const mine = m.author_id === user?.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2 ${mine ? "brand-gradient text-white" : "bg-accent"}`}>
                      {!mine && <p className="text-xs font-semibold text-secondary mb-0.5">{m.author}</p>}
                      <p className="text-sm">{m.text}</p>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t border-border flex gap-2">
              <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message..." data-testid="chat-input" />
              <Button onClick={send} className="brand-gradient text-white border-0" data-testid="send-message-btn"><Send size={16} /></Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="announcements" className="mt-5">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-heading font-semibold text-lg mb-4">Post announcement</h3>
              <Input value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} placeholder="Title" className="mb-3" data-testid="ann-title-input" />
              <Textarea value={annForm.body} onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })} placeholder="Message to all parents..." rows={4} data-testid="ann-body-input" />
              <label className="flex items-center gap-2 mt-3 text-sm text-muted-foreground cursor-pointer">
                <Checkbox checked={emailParents} onCheckedChange={(v) => setEmailParents(!!v)} data-testid="ann-email-checkbox" />
                <Mail size={14} /> Also email all parents
              </label>
              <Button onClick={postAnn} className="mt-4 brand-gradient text-white border-0 hover:opacity-90" data-testid="post-ann-btn"><Megaphone size={16} className="mr-1.5" /> Post</Button>
            </div>
            <div className="space-y-3">
              {announcements.length === 0 && <p className="text-sm text-muted-foreground">No announcements yet.</p>}
              {announcements.map((a) => (
                <div key={a.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`ann-${a.id}`}>
                  <p className="font-heading font-semibold">{a.title}</p>
                  <p className="text-sm text-muted-foreground mt-1">{a.body}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <p className="text-xs text-muted-foreground">{a.created_at?.slice(0, 10)}</p>
                    <span className="text-xs text-secondary font-medium" data-testid={`ann-seen-${a.id}`}>👁 Seen by {a.seen_count || 0}/{a.total_parents || 0} parents</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
